import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { bearAuthClient } from "@/lib/bear-auth-client";
import { RequireSignIn, useSession, useSignedInUser } from "@/lib/session";

export const Route = createFileRoute("/invite/$invitationId")({
  component: () => (
    <RequireSignIn>
      <AcceptInvitation />
    </RequireSignIn>
  ),
});

/**
 * Joining an organization from an invitation link. Better Auth only lets the
 * invited email accept, so a forwarded link is useless to anyone else, and
 * an invitee never needs an organization of their own first.
 */
function AcceptInvitation() {
  const { invitationId } = Route.useParams();
  const user = useSignedInUser();
  const { switchOrganization } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invitation = useQuery({
    queryKey: ["invitation", invitationId],
    retry: false,
    queryFn: async () => {
      const result = await bearAuthClient.organization.getInvitation({
        query: { id: invitationId },
      });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });

  async function respond(accept: boolean) {
    setBusy(true);
    setError(null);
    const result = accept
      ? await bearAuthClient.organization.acceptInvitation({ invitationId })
      : await bearAuthClient.organization.rejectInvitation({ invitationId });
    if (result.error) {
      setError(result.error.message ?? "Could not answer the invitation.");
      setBusy(false);
      return;
    }
    if (accept && invitation.data) {
      await switchOrganization(invitation.data.organizationId);
    }
    await navigate({ to: "/" });
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6 py-12 text-center">
      {invitation.isPending ? (
        <p className="text-muted-foreground">Loading invitation…</p>
      ) : invitation.data ? (
        <>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">
              Join {invitation.data.organizationName}
            </h1>
            <p className="text-sm text-muted-foreground">
              {invitation.data.inviterEmail} invited {invitation.data.email}.
              You&apos;ll see and edit all of the organization&apos;s projects.
            </p>
          </div>
          <div className="flex justify-center gap-2">
            <Button disabled={busy} onClick={() => void respond(true)}>
              Join organization
            </Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void respond(false)}
            >
              Decline
            </Button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">Invitation unavailable</h1>
          <p className="text-sm text-muted-foreground">
            It may have expired or been cancelled, or it was sent to a different
            email than {user.email}. Ask for a new invitation.
          </p>
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Link to="/" className="text-sm underline">
        Back to projects
      </Link>
    </main>
  );
}
